import { AiPanel } from "./AiPanel";
import type { AiDraft } from "./AiPanel";
import { CRICKETS } from "./crickets";
import type { Dict } from "./i18n";
import { slotTheme } from "./theme";

export type SideKind = "rules" | "ai";

export interface SideDraft {
  readonly kind: SideKind;
  /** Built-in bot id when kind is `rules`. */
  readonly botId: string;
}

/**
 * Chooses both fighters for the next match.
 *
 * Each side can be a built-in rule bot or a live AI cricket. AI vs AI is
 * allowed — that is the point of the product — so each AI side carries its own
 * draft (key, model, ring name) rather than sharing a single session cricket.
 */
export function StartDuelModal({
  d,
  left,
  right,
  leftDraft,
  rightDraft,
  onChangeLeft,
  onChangeRight,
  onChangeLeftDraft,
  onChangeRightDraft,
  onStart,
  onClose,
}: {
  d: Dict;
  left: SideDraft;
  right: SideDraft;
  leftDraft: AiDraft;
  rightDraft: AiDraft;
  onChangeLeft: (next: SideDraft) => void;
  onChangeRight: (next: SideDraft) => void;
  onChangeLeftDraft: (next: AiDraft) => void;
  onChangeRightDraft: (next: AiDraft) => void;
  onStart: () => void;
  onClose: () => void;
}) {
  const leftReady = sideReady(left, leftDraft);
  const rightReady = sideReady(right, rightDraft);
  const canStart = leftReady && rightReady && !sameRulesPick(left, right);

  return (
    <div
      className="duel-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="duel-modal-title"
      onClick={onClose}
    >
      <div className="duel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="duel-modal-head">
          <h2 id="duel-modal-title">{d.duelModalTitle}</h2>
          <button className="ghost duel-modal-close" onClick={onClose} aria-label={d.close}>
            ×
          </button>
        </div>
        <p className="duel-modal-intro">{d.duelModalIntro}</p>

        <div className="duel-sides">
          <SideColumn
            slot={0}
            label={d.duelSideLeft}
            side={left}
            draft={leftDraft}
            onChangeSide={onChangeLeft}
            onChangeDraft={onChangeLeftDraft}
            d={d}
          />
          <div className="duel-vs" aria-hidden>
            VS
          </div>
          <SideColumn
            slot={1}
            label={d.duelSideRight}
            side={right}
            draft={rightDraft}
            onChangeSide={onChangeRight}
            onChangeDraft={onChangeRightDraft}
            d={d}
          />
        </div>

        {sameRulesPick(left, right) ? (
          <p className="duel-warning">{d.duelSameFighter}</p>
        ) : null}

        <div className="duel-footer">
          <button className="ghost" onClick={onClose}>
            {d.close}
          </button>
          <button className="primary" onClick={onStart} disabled={!canStart}>
            {d.duelStart}
          </button>
        </div>
      </div>
    </div>
  );
}

function SideColumn({
  slot,
  label,
  side,
  draft,
  onChangeSide,
  onChangeDraft,
  d,
}: {
  slot: 0 | 1;
  label: string;
  side: SideDraft;
  draft: AiDraft;
  onChangeSide: (next: SideDraft) => void;
  onChangeDraft: (next: AiDraft) => void;
  d: Dict;
}) {
  const theme = slotTheme(slot);
  return (
    <div className="duel-side" style={{ borderColor: theme.dim }}>
      <div className="duel-side-head">
        <span className="swatch" style={{ background: theme.color, boxShadow: `0 0 8px ${theme.glow}` }} />
        <span className="duel-side-label">{label}</span>
      </div>

      <div className="duel-kind-tabs" role="tablist" aria-label={label}>
        <button
          type="button"
          role="tab"
          aria-selected={side.kind === "rules"}
          className={side.kind === "rules" ? "active" : ""}
          onClick={() => onChangeSide({ ...side, kind: "rules" })}
        >
          {d.duelKindRules}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={side.kind === "ai"}
          className={side.kind === "ai" ? "active" : ""}
          onClick={() => onChangeSide({ ...side, kind: "ai" })}
        >
          {d.duelKindAi}
        </button>
      </div>

      {side.kind === "rules" ? (
        <div className="duel-bot-list">
          {CRICKETS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`duel-bot ${side.botId === c.id ? "active" : ""}`}
              onClick={() => onChangeSide({ kind: "rules", botId: c.id })}
            >
              <span className="duel-bot-name">{c.persona}</span>
              <span className="duel-bot-blurb">
                {c.blurbKey === "collector"
                  ? d.blurbCollector
                  : c.blurbKey === "scout"
                    ? d.blurbScout
                    : d.blurbHunter}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="duel-ai-embed">
          <AiPanel
            draft={draft}
            onChange={onChangeDraft}
            active={false}
            d={d}
            formOnly
            title={d.duelAiConfigTitle}
            idPrefix={slot === 0 ? "duel-a" : "duel-b"}
          />
        </div>
      )}
    </div>
  );
}

function sideReady(side: SideDraft, draft: AiDraft): boolean {
  if (side.kind === "rules") return CRICKETS.some((c) => c.id === side.botId);
  return draft.apiKey.trim() !== "" && draft.model.trim() !== "";
}

function sameRulesPick(left: SideDraft, right: SideDraft): boolean {
  return left.kind === "rules" && right.kind === "rules" && left.botId === right.botId;
}

export function defaultSideDraft(botId: string): SideDraft {
  return { kind: "rules", botId };
}
